import { backofficeDialogOptions } from 'src/app/layout/shared/dialogs/backoffice-dialog.options';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CurrencyRS } from 'src/app/core/models/site-settings/currency-rs';
import { SiteCurrencies } from 'src/app/core/models/site-settings/site-currencies';
import { CurrencyService } from 'src/app/core/services/api/currency.service';
import { SiteSettingService } from 'src/app/core/services/api/site-setting.service';
import { AddCurrencyModalComponent } from '../../../ecommerce/shop-settings/shop-currencies/add-currency-modal/add-currency-modal.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-currency-change',
  templateUrl: './currency-change.component.html',
  styleUrls: ['./currency-change.component.scss']
})
export class CurrencyChangeComponent implements OnInit {

  @Input() siteId: string;
  @Input() modalTitle: string = 'sh_setting_change_shop_currency';
  @Input() buttonText: string = 'Change Currency';
  @Input() buttonClass: string = 'btn btn-outline-primary';
  @Input() siteCurrency: SiteCurrencies;
  @Input() defaultCurrency: CurrencyRS = {};
  @Output() currencyChanged = new EventEmitter<CurrencyRS>();

  availableCurrencies: Array<CurrencyRS> = [];

  constructor(
    private modalService: NgbModal,
    private siteSettingService: SiteSettingService,
    private currencyService: CurrencyService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    // No need to call getSiteCurrency here as it will be passed from parent
  }

  initializeCurrencyData(siteCurrency: SiteCurrencies, defaultCurrency: CurrencyRS) {
    this.siteCurrency = siteCurrency;
    this.defaultCurrency = defaultCurrency;
  }

  getSiteCurrency() {
    // This method is kept for backward compatibility but should not be called
    // when currency data is passed from parent
    if (!this.siteCurrency || !this.defaultCurrency.id) {
      this.siteSettingService.getSiteCurrency(this.siteId).subscribe(
        (res) => {
          this.siteCurrency = JSON.parse(JSON.stringify(res));
          this.defaultCurrency = this.siteCurrency.supported_currencies.find(
            (x) => x.default_currency === true
          );
        },
        (error) => {}
      );
    }
  }

  getAllCurrencies() {
    this.currencyService.getAllCurrencies().subscribe(
      (res) => {
        this.availableCurrencies = JSON.parse(JSON.stringify(res));
      },
      (error) => {}
    );
  }

  onChangeCurrencyClick() {
    // Load all currencies only when user wants to change currency
    if (this.availableCurrencies.length === 0) {
      this.currencyService.getAllCurrencies().subscribe(
        (res) => {
          this.availableCurrencies = JSON.parse(JSON.stringify(res));
          this.openCurrencyModal();
        },
        (error) => {
          this.toastr.error('Failed to load currencies', 'Error');
        }
      );
    } else {
      this.openCurrencyModal();
    }
  }

  private openCurrencyModal() {
    const modalRef = this.modalService.open(AddCurrencyModalComponent, backofficeDialogOptions('currency', 'standard'));
    modalRef.componentInstance.modalTitle = this.modalTitle;
    modalRef.componentInstance.dataObject = this.availableCurrencies;
    modalRef.componentInstance.selectedObject = this.defaultCurrency.id;
    modalRef.componentInstance.labelReference = 'code';
    modalRef.result.then(
      (result) => {
        if (result) {
          this.siteCurrency.supported_currencies = [];
          this.siteCurrency.supported_currencies.push(result);
          this.defaultCurrency = result;
          this.updateShopCurrency();
        }
      },
      (reason) => {
        // Modal dismissed
      }
    );
  }

  updateShopCurrency() {
    this.siteSettingService.updateSiteCurrencies(this.siteId, this.siteCurrency).subscribe(
      (res) => {
        this.siteCurrency = JSON.parse(JSON.stringify(res));
        this.defaultCurrency = this.siteCurrency.supported_currencies.find(
          (x) => x.default_currency === true
        );
        
        // Show different success messages based on context
        const successMessage = this.modalTitle === 'sh_setting_add_currency' 
          ? this.translate.instant('currency_saved')
          : this.translate.instant('currency_changed');
        
        this.toastr.success(successMessage, this.translate.instant('success'));
        this.currencyChanged.emit(this.defaultCurrency);
      },
      (error) => {
        this.toastr.error(error.error.message, this.translate.instant('error'));
      }
    );
  }

  getCurrentCurrency(): CurrencyRS {
    return this.defaultCurrency;
  }

  getSiteCurrencies(): SiteCurrencies {
    return this.siteCurrency;
  }
}
